import type { CurrencyFeesResponse, Translated } from "@repo/shared/types";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCurrencyFees, useUpdateCurrencyFees } from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./currency-fees-card.i18n";

export function CurrencyFeesCard({ portfolioCollectionId }: { portfolioCollectionId: string }) {
  const t = useT(T);
  const { currencyFees, isLoading } = useCurrencyFees(portfolioCollectionId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading || !currencyFees ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t.saving}
          </div>
        ) : (
          // Keyed by the loaded values so the form initialises its inputs once.
          <CurrencyFeesForm
            key={`${currencyFees.buyFxFeePct}-${currencyFees.sellFxFeePct}`}
            portfolioCollectionId={portfolioCollectionId}
            initial={currencyFees}
            t={t}
          />
        )}
      </CardContent>
    </Card>
  );
}

function CurrencyFeesForm({
  portfolioCollectionId,
  initial,
  t,
}: {
  portfolioCollectionId: string;
  initial: CurrencyFeesResponse;
  t: Translated<typeof T>;
}) {
  const updateFees = useUpdateCurrencyFees();
  const isPending = updateFees.isPending;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [buy, setBuy] = useState(initial.buyFxFeePct === null ? "" : String(initial.buyFxFeePct));
  const [sell, setSell] = useState(
    initial.sellFxFeePct === null ? "" : String(initial.sellFxFeePct),
  );

  // Empty input = "none" (null). Otherwise must be a number in (0, 5].
  const parseFee = (value: string): number | null | "invalid" => {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0 || n > 5) return "invalid";
    return n;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const buyFxFeePct = parseFee(buy);
    const sellFxFeePct = parseFee(sell);
    if (buyFxFeePct === "invalid" || sellFxFeePct === "invalid") {
      setError(t.invalid);
      return;
    }

    updateFees.mutate(
      { id: portfolioCollectionId, data: { buyFxFeePct, sellFxFeePct } },
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
        },
        onError: (err) => setError(err.message || t.failedToSave),
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
      )}
      {success && (
        <div className="text-sm text-success bg-success/10 p-3 rounded-md">{t.saved}</div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="buyFxFee">{t.buyFee}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="buyFxFee"
              type="number"
              step="0.01"
              min="0.01"
              max="5"
              value={buy}
              onChange={(e) => setBuy(e.target.value)}
              placeholder={t.none}
              disabled={isPending}
              className="w-24"
            />
            <span className="text-muted-foreground">%</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sellFxFee">{t.sellFee}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="sellFxFee"
              type="number"
              step="0.01"
              min="0.01"
              max="5"
              value={sell}
              onChange={(e) => setSell(e.target.value)}
              placeholder={t.none}
              disabled={isPending}
              className="w-24"
            />
            <span className="text-muted-foreground">%</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" loading={isPending}>
          {t.save}
        </Button>
      </div>
    </form>
  );
}
